import type * as Tensorflow from '@tensorflow/tfjs';
import { CustomModelIOHandler } from 'obfuscated/inquiry/lib/clients/CustomModel/CustomModelIOHandler';
import { decryptPayload } from 'obfuscated/inquiry/lib/utils/crypto';

import { fetchModelSrc } from 'lib/assets';

const MODELS = ['custom-1'] as const;

type ModelName = (typeof MODELS)[number];

class Client {
  public tf?: typeof Tensorflow;
  public model?: Tensorflow.GraphModel;
  public errored = false;

  public isLoaded() {
    return !!this.model;
  }

  /**
   * Fetch and decrypt the model and save its instance to this client. Note that the Tensorflow
   * module is dependency injected rather than imported because this code is obfuscated and
   * bundled separately from the rest of the inquiry bundle; this causes all imports to be
   * duplicated. Using dependency injection helps reduce the size of the obfuscated bundle.
   *
   * @param tf Tensorflow instance, dependency injected into this client
   * @param modelName Type of model to fetch
   */
  public async load(tf: typeof Tensorflow, modelName: ModelName): Promise<void> {
    const modelPath = fetchModelSrc(modelName);

    if (modelPath == null) {
      throw new Error('model name not valid');
    }

    if (this.errored || this.isLoaded()) {
      return Promise.resolve();
    }

    this.tf = tf;

    await tf.ready();

    // We should only load custom models on webgl backend because they're computationally expensive
    // and the wasm backend is not as optimized for this kind of workload.
    // See https://github.com/tensorflow/tfjs/tree/master/tfjs-backend-wasm#when-should-i-use-the-wasm-backend
    if (tf.getBackend() !== 'webgl') {
      throw new Error('Cannot load model with non-webgl backend');
    }

    const response = await fetch(`${modelPath}/manifest.enc`);
    const text = await response.text();
    const decoded = await decryptPayload(text, true);
    const json = JSON.parse(decoded);

    this.model = await tf.loadGraphModel(new CustomModelIOHandler(tf, json, `${modelPath}/`));
  }

  /**
   * Warm up the model with a dummy input to avoid first inference latency. Suggested from
   * TensorFlow docs:
   * https://www.tensorflow.org/js/guide/platform_environment#shader_compilation_texture_uploads
   */
  public warmup() {
    if (this.errored) return;

    if (!this.model) {
      throw new Error('Model not loaded');
    }

    if (!this.tf) {
      throw new Error('Missing Tensorflow reference');
    }

    if (this.model.inputs[0].shape) {
      const dummyInput = this.tf.randomUniform(this.model.inputs[0].shape, 0, 1, 'float32');
      const warmupResults = this.model.execute(dummyInput);
      this.tf.dispose([warmupResults, dummyInput]);
    }
  }

  /**
   * Run inference on model and return boxes & scores. A lot of this code was inspired by
   * https://github.com/Hyuto/yolov8-seg-tfjs
   *
   * @param source The video element to run inference on
   */
  public async detect(source: HTMLVideoElement): Promise<{ box: number[]; score: number }[]> {
    if (!this.model) {
      throw new Error('Model not loaded');
    }

    if (!this.tf) {
      throw new Error('Missing Tensorflow reference');
    }

    const shape = this.model.inputs[0].shape;
    if (this.errored || !shape) {
      return Promise.resolve([]);
    }

    const [modelHeight, modelWidth] = shape.slice(1, 3);
    const input = this.preprocess(this.tf, source, modelWidth, modelHeight);

    const tf = this.tf;
    tf.engine().startScope();

    const result = this.model.execute(input) as Tensorflow.Tensor[];

    // The scores and classes are in result[1], the segmentation mask is in result[0]
    const resultTransposed = this.tf.tidy(() => result[1].transpose([0, 2, 1]).squeeze());

    const boxes = tf.tidy(() => {
      const w = resultTransposed.slice([0, 2], [-1, 1]);
      const h = resultTransposed.slice([0, 3], [-1, 1]);
      const x1 = tf.sub(resultTransposed.slice([0, 0], [-1, 1]), tf.div(w, 2));
      const y1 = tf.sub(resultTransposed.slice([0, 1], [-1, 1]), tf.div(h, 2));
      return tf.concat([y1, x1, tf.add(y1, h), tf.add(x1, w)], 1).squeeze();
    }) as Tensorflow.Tensor2D; // [y1, x1, y2, x2]

    const [scores, classes] = this.tf.tidy(() => {
      // Note that here is only 1 class in id_segmentation so the size of the slice is [-1,1];
      // if there are more classes it would be [-1, numClasses]
      const rawScores = resultTransposed.slice([0, 4], [-1, 1]);
      return [rawScores.max(1), rawScores.argMax(1)];
    });

    // These values for NMS are somewhat arbitrarily chosen but also kind of follow verif_tools_signature_detector
    const nms = await tf.image.nonMaxSuppressionAsync(boxes, scores, 200, 0.45, 0.2);
    const output = tf.tidy(() =>
      tf.concat(
        [
          boxes.gather(nms, 0),
          scores.gather(nms, 0).expandDims(1),
          classes.gather(nms, 0).expandDims(1),
        ],
        1,
      ),
    );

    const outputData = [];

    for (let i = 0; i < output.shape[0]; i++) {
      // get every first 5 elements from every row - the 6th element contains the class but
      // since id_segmentation only has 1 class we can ignore it.
      const rowData = output.slice([i, 0], [1, 5]);
      const [y1, x1, y2, x2, score] = rowData.dataSync();
      outputData.push({
        box: [x1, y1, x2, y2],
        score,
      });
    }

    tf.engine().endScope();

    return outputData;
  }

  private preprocess(
    tf: typeof Tensorflow,
    source: HTMLVideoElement,
    modelWidth: number,
    modelHeight: number,
  ): Tensorflow.Tensor {
    return tf.tidy(() => {
      const img = tf.browser.fromPixels(source);

      // padding image to square
      const [height, width] = img.shape.slice(0, 2);
      const maxSize = Math.max(width, height);
      const imgPadded = img.pad([
        [0, maxSize - height],
        [0, maxSize - width],
        [0, 0],
      ]) as Tensorflow.Tensor3D;

      return tf.image
        .resizeBilinear(imgPadded, [modelWidth, modelHeight]) // resize frame
        .div(255.0) // normalize
        .expandDims(0); // add batch
    });
  }
}

export const CustomModelClient = new Client();
